import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import * as S from 'components/form/FormItem.styled';
import Authorization from 'components/global/Authorization';
import ComponentErrorBoundary from 'components/global/ComponentErrorBoundary';
import Icon from 'components/global/Icon';
import Skeleton from 'components/global/Skeleton';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

/**
 * @typedef {Object} colSpan
 * @property {number} [xs] - The number of columns that the item will take up on extra small screens.
 * @property {number} [sm] - The number of columns that the item will take up on small screens.
 * @property {number} [md] - The number of columns that the item will take up on medium screens.
 * @property {number} [lg] - The number of columns that the item will take up on large screens.
 * @property {number} [xl] - The number of columns that the item will take up on extra large screens.
 * @property {number} [xx] - The number of columns that the item will take up on extra extra large screens.
 
/**
* @typedef {Object} colSpanChild
 * @property {number} [xs] - The number of columns that the item will take up on extra small screens.
 * @property {number} [sm] - The number of columns that the item will take up on small screens.
 * @property {number} [md] - The number of columns that the item will take up on medium screens.
 * @property {number} [lg] - The number of columns that the item will take up on large screens.
 * @property {number} [xl] - The number of columns that the item will take up on extra large screens.
 * @property {number} [xx] - The number of columns that the item will take up on extra extra large screens.
 */

/**
 * @author @buunsal
 * @description FormItem is a component that is used to render a form item it handles col,labeling etc...
 * @param {object}	props
 * @param {string} [props.col=12] - Column size of the form item.
 * @param {string} [props.colChild=12] - Column size of the form children container except label.
 * @param {colSpan} props.colSpan - Column span of the form item.
 * @param {colSpanChild} props.colSpanChild - Column span of the form children container except label.
 * @param {string} props.label - Label of the form item.
 * @param {string} props.description - Sub description of label.
 * @param {string} props.toolTip - Tooltip of the form item.
 * @param {string} props.color - Color of the label.
 * @param {boolean} props.required - If true, the form item is required.
 * @param {boolean} props.isLoading - If true, the form item is loading.
 * @param {boolean} props.viewMode - Set Viewmode look for formItem.
 * @param {object} props.style - Custom JSX styles.
 * @param {object} props.childStyle - Custom JSX styles.
 * @param {string} props.roleName - Role name of the form item.
 * @param {"column" | "row" | "column-reverse" | "row-reverse"} [props.direction = "column"] - Direction of the form item.
 * @param {string} props.className - Class name of the form item.
 * @param {string} props.classNameChild - Class name of the form item.

 * @returns {JSX.Element} - Wrapped input components.
 */
const FormItem = ({
	col = '12',
	colSpan = undefined,
	colChild = '12',
	colSpanChild = undefined,
	label,
	toolTip,
	description,
	required,
	color,
	viewMode = false,
	isLoading = false,
	children,
	style,
	childStyle,
	className,
	classNameChild,
	roleName,
	customLabelContent,
	direction = 'column'
}) => {
	const { t } = useTranslation();
	const [isLoaded, setIsLoaded] = useState(false);

	const getSpan = ({ sm, md, lg, xl, xx }) => {
		const clsSM = sm ? `col-sm-${sm}` : '';
		const clsMD = md ? `col-md-${md}` : '';
		const clsLG = lg ? `col-lg-${lg}` : '';
		const clsXL = xl ? `col-xl-${xl}` : '';
		const clsXX = xx ? `col-xxl-${xx}` : '';

		return `${clsSM} ${clsMD} ${clsLG} ${clsXL} ${clsXX}`;
	};

	const spanParent = getSpan(colSpan ? colSpan : {});
	const spanChild = getSpan(colSpanChild ? colSpanChild : {});

	useEffect(() => {
		if (isLoaded) return;

		if (!isLoading) {
			setIsLoaded(true);
		}
	}, [isLoading]);

	return (
		<Authorization path={roleName}>
			<S.FormItemContainer
				direction={direction}
				style={style}
				className={`form-item col-${col} ${spanParent} ${className ?? ''}`}>
				{(label || description) && (
					<S.TextContainer>
						{label && (
							<Typography fontWeight="500" component="label" noWrap>
								<span style={{ color }}>{t(label)}</span>
								{required && !viewMode && <S.Required>*</S.Required>}{' '}
								{toolTip && (
									<Tooltip title={toolTip} placement="bottom">
										<Icon icon={faCircleInfo} />
									</Tooltip>
								)}
								{customLabelContent ? customLabelContent : undefined}
							</Typography>
						)}

						{description && (
							<Typography color="var(--content-2)" noWrap>
								{description}
							</Typography>
						)}
					</S.TextContainer>
				)}
				{isLoading && (
					<div
						style={childStyle}
						className={`form-item-content col-${colChild} ${spanChild} ${
							classNameChild ?? ''
						} formitem-${direction}`}>
						<Skeleton width="100%" height="4.5rem" />
					</div>
				)}
				{isLoaded && (
					<div
						style={{
							...childStyle,
							...(isLoading ? { display: 'none' } : {})
						}}
						className={`form-item-content col-${colChild} ${spanChild} ${
							classNameChild ?? ''
						} formitem-${direction}`}>
						<ComponentErrorBoundary
							fallback={
								<FallbackComponent name={children?.props?.name} value={children?.props?.value} />
							}>
							{children}
						</ComponentErrorBoundary>
					</div>
				)}
			</S.FormItemContainer>
		</Authorization>
	);
};

const FallbackComponent = ({ name, value }) => {
	const { t } = useTranslation();

	const formMethods = useFormContext();
	const errorValue = value ?? (name && formMethods?.getValues?.(name));

	return (
		<Typography color="var(--red)">
			{errorValue
				? t('unexpectedErrorOccuredForThisValue', {
						value: errorValue
					})
				: 'unexpectedErrorOccured'}
		</Typography>
	);
};

export default FormItem;
